import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { telemetryService } from "npo-common";
import { connect } from "react-redux";
import { formatLongDate } from "src/common/utilities/formatLongDate";
import localeService from "src/dataServices/LocaleService";
import { IApplicationState } from "src/state";
import { IFeatureFlagsState } from "src/state/feature-flags";

interface IPropsFromState {
  Profile: any;
  featureFlags: IFeatureFlagsState;
  revalidationTriggered: boolean;
}

interface IPropsFromParent {}

export type IOrgRegistrationInfoProps = IPropsFromParent & IPropsFromState;

export const OrgRegistrationInfo = ({ Profile, featureFlags, revalidationTriggered }: IOrgRegistrationInfoProps) => {
  // Right after the user triggers revalidation, the backend status can lag and still report the
  // initial "validation" state. In that brief window force the "revalidation" message so the user
  // sees the correct status immediately. On a fresh page load the flag is reset and the (now
  // up-to-date) backend profileState is used.
  const normalizedProfileState = revalidationTriggered
    ? "InProgressRevalidation"
    : Profile?.profileState?.trim?.().replace(/,$/, "");
  const profileStateContent = normalizedProfileState
    ? localeService.getContent("ProfileV2", "ProfileState")?.[normalizedProfileState]
    : undefined;
  const profileReason = profileStateContent?.Reason?.trim?.();
  const profileEligibilityLink = profileStateContent?.EligibilityLink?.trim?.();
  const profileAction = profileStateContent?.Action?.trim?.();
  const profileActionLink = profileStateContent?.ActionLink?.trim?.();

  const renderProfileReason = () => {
    const parts = profileReason?.match(/^(.*)<0>(.*)<\/0>(.*)$/);

    if (!parts || !profileEligibilityLink) {
      return <>{profileReason}</>;
    }

    return (
      <>
        {parts[1]}
        <a href={profileEligibilityLink} target="_blank" rel="noreferrer">
          {parts[2]}
        </a>
        {parts[3]}
      </>
    );
  };

  const renderProfileAction = () => {
    const parts = profileAction?.match(/^(.*)<0>(.*)<\/0>(.*)$/);

    if (!parts || !profileActionLink) {
      return <>{profileAction}</>;
    }

    return (
      <>
        {parts[1]}
        <a href={profileActionLink} target="_blank" rel="noreferrer">
          {parts[2]}
        </a>
        {parts[3]}
      </>
    );
  };

  return (
    <section id="profile-org-registration-information">
      <div id="profile-org-registration-information-description">
        <p>{localeService.getText("ProfileV2", "OrgRegistrationInformationDescription")}</p>
      </div>
      <div id="profile-org-registration-information" className="ms-Grid">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg6 ms-xl6">
            <p>{localeService.getText("ProfileV2", "OrgStatus")}</p>
            <p>
              <b>{Profile.status === "DeclinedFR" ? "Declined" : Profile.status}</b>
            </p>
          </div>
          {featureFlags.profilePageEnhancementsEnabled && profileReason && (
            <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg6 ms-xl6">
              <p>{localeService.getText("ProfileV2", "OrgReason")}</p>
              <p>
                <b>{renderProfileReason()}</b>
              </p>
            </div>
          )}
        </div>

        {featureFlags.profilePageEnhancementsEnabled && profileAction && (
          <div>
            <p>
              <b className="glyph-prepend glyph-prepend-warning text-red">{localeService.getText("ProfileV2", "OrgActionRequired")}</b>
            </p>
            <p>              
              <b>{renderProfileAction()}</b>
            </p>
          </div>
        )}

        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg6 ms-xl6">
            <p>{localeService.getText("ProfileV2", "OrgDomain")}</p>
            <p>
              <b>{Profile.domain}</b>
            </p>
          </div>
          <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg6 ms-xl6">
            <p>{localeService.getText("ProfileV2", "OrgEffectiveDate")}</p>
            <p>
              <b>{formatLongDate(Profile.effectiveDateTime)}</b>
            </p>
          </div>
        </div>

        {featureFlags.profilePageEnhancementsEnabled && ( 
        <div className="ms-Grid-row">
          {Profile?.profileLastUpdatedDate && (
            <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg6 ms-xl6">
              <p>{localeService.getText("ProfileV2", "OrgLastUpdatedDate")}</p>
              <p>
                <b>{formatLongDate(Profile.profileLastUpdatedDate)}</b>
              </p>
            </div>
        )}
          {Profile?.profileLastUpdatedBy && (
            <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg6 ms-xl6">
              <p>{localeService.getText("ProfileV2", "OrgLastUpdatedBy")}</p>
              <p>
                <b>{Profile.profileLastUpdatedBy}</b>
              </p>
            </div>
          )}
        </div>
        )}

      </div>
    </section>
  );
};
const mapStateToProps = ({ profile, flags }: IApplicationState) => ({
  Profile: profile.profile,
  featureFlags: flags,
  revalidationTriggered: !!profile.revalidationTriggered
});
export default connect(mapStateToProps)(withAITracking(telemetryService.aiReactPlugin, OrgRegistrationInfo));
